import { createElement, type ReactNode, useCallback, useEffect, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { getUploadById } from '@/features/uploads/data';
import { ApiError } from '@/shared/data/client';
import { consumeUploadToastIntent } from '@/shared/notifications/uploadToastIntent';
import { Inline, Link, Text } from '@/ui';

type UseResultsFileUploadPollingParams = {
  selectedOrganisationId: string | null;
  onRefresh?: () => void;
  invalidateUploadRelatedTables: () => Promise<void>;
  notify: (args: { message: ReactNode; tone: 'blue' | 'green' | 'orange' | 'red' }) => void;
};

const buildUploadToastMessage = ({ label, href }: { label: string; href: string }): ReactNode =>
  createElement(
    Inline,
    { gap: 'sm', align: 'center' },
    createElement(Text, { role: 'body', component: 'span', children: label }),
    createElement(Link, {
      component: RouterLink,
      to: href,
      underline: 'always',
      color: 'inherit',
      children: 'View',
    }),
  );

export const useResultsFileUploadPolling = ({
  selectedOrganisationId,
  onRefresh,
  invalidateUploadRelatedTables,
  notify,
}: UseResultsFileUploadPollingParams): ((uploadId: string) => void) => {
  const resultsFilePollRef = useRef<Set<string>>(new Set());
  const resultsFilePollTimeoutRef = useRef<Partial<Record<string, ReturnType<typeof setTimeout>>>>(
    {},
  );
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      Object.values(resultsFilePollTimeoutRef.current).forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      resultsFilePollTimeoutRef.current = {};
      resultsFilePollRef.current.clear();
    };
  }, []);

  return useCallback(
    (uploadId: string) => {
      if (resultsFilePollRef.current.has(uploadId)) return;
      resultsFilePollRef.current.add(uploadId);
      const maxAttempts = 20;
      const delayMs = 3000;
      let attempt = 0;
      const stopPolling = (): void => {
        const timeoutId = resultsFilePollTimeoutRef.current[uploadId];
        if (timeoutId !== undefined) {
          clearTimeout(timeoutId);
          resultsFilePollTimeoutRef.current[uploadId] = undefined;
        }
        resultsFilePollRef.current.delete(uploadId);
      };

      const run = async (): Promise<void> => {
        if (!isMountedRef.current) {
          stopPolling();
          return;
        }
        attempt += 1;
        try {
          const upload = await getUploadById(uploadId, selectedOrganisationId ?? undefined);
          const resultsFileSlug = upload.resultsFileSlug ?? null;
          const fileLabel = upload.fileName || 'results';
          const showFallbackToast = (): void => {
            if (!consumeUploadToastIntent(uploadId)) {
              return;
            }
            if (upload.status === 'completed') {
              const href = resultsFileSlug
                ? `/results-files/${resultsFileSlug}`
                : `/uploads/${uploadId}`;
              notify({
                message: buildUploadToastMessage({
                  label: `${fileLabel} uploaded`,
                  href,
                }),
                tone: 'green',
              });
              return;
            }
            const errorSuffix = upload.error ? ` - ${upload.error}` : '';
            const message =
              upload.status === 'completed_with_errors'
                ? `Upload completed with errors: ${fileLabel}${errorSuffix}.`
                : `Upload failed: ${fileLabel}${errorSuffix}.`;
            notify({
              message: buildUploadToastMessage({
                label: message,
                href: `/uploads/${uploadId}`,
              }),
              tone: upload.status === 'completed_with_errors' ? 'orange' : 'red',
            });
          };
          if (resultsFileSlug) {
            await invalidateUploadRelatedTables();
            showFallbackToast();
            onRefresh?.();
            stopPolling();
            return;
          }
          const status = upload.status;
          const isTerminal =
            status === 'completed' || status === 'completed_with_errors' || status === 'failed';
          if (isTerminal) {
            await invalidateUploadRelatedTables();
            showFallbackToast();
            onRefresh?.();
            stopPolling();
            return;
          }
        } catch (error) {
          if (error instanceof ApiError) {
            const authFailed =
              error.status === 401 || error.status === 403 || error.code === 'AUTH_FAILED';
            if (authFailed) {
              stopPolling();
              return;
            }
          }
        }

        if (attempt < maxAttempts) {
          resultsFilePollTimeoutRef.current[uploadId] = setTimeout(() => {
            void run();
          }, delayMs);
          return;
        }
        stopPolling();
      };

      void run();
    },
    [invalidateUploadRelatedTables, notify, onRefresh, selectedOrganisationId],
  );
};
